import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown as ChevronDownIcon, Menu as MenuIcon, X as CloseIcon } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import { NavigatorButton } from 'components/ui/navigator-button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from 'components/ui/sheet';
import { getPrimaryNavItems, NavItem } from 'components/layout/navigationData';
import { useAuth } from 'contexts/AuthContext';
import { cn } from 'lib/utils';
import { Icon } from '../icons/Icon';
import { useEventTracking } from 'hooks/useEventTracking';
import { nativeTapFeedback } from 'mobile/nativeShell';
import { DEFAULT_PROFILE_COLOR, getContrastingTextColor } from 'utils/color';
import './HeaderSection.css';

export const HeaderSection: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  const { track } = useEventTracking();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navItems = useMemo(() => {
    const cohorts = user?.cohorts ?? [];
    return getPrimaryNavItems({
      cohorts,
    });
  }, [user?.cohorts]);
  const mobilePrimaryNav = useMemo(() => navItems.slice(0, 3), [navItems]);
  const canAccessOrganizationAdmin = Boolean(
    user?.is_staff || (user?.organization?.id && user?.organization_role === 'admin')
  );

  const initials = useMemo(() => {
    const firstInitial = user?.first_name?.trim().charAt(0);
    const lastInitial = user?.last_name?.trim().charAt(0);

    if (firstInitial && lastInitial) {
      return `${firstInitial}${lastInitial}`.toUpperCase();
    }
    if (firstInitial) {
      return firstInitial.toUpperCase();
    }
    if (lastInitial) {
      return lastInitial.toUpperCase();
    }
    const usernameInitial = user?.username?.trim().charAt(0);
    if (usernameInitial) {
      return usernameInitial.toUpperCase();
    }
    return 'WF';
  }, [user?.first_name, user?.last_name, user?.username]);
  const avatarBackground = user?.profile_color ?? DEFAULT_PROFILE_COLOR;
  const avatarTextColor = getContrastingTextColor(avatarBackground);
  const avatarUrl = user?.avatar_url;
  const organizationLogoUrl = user?.organization?.icon_url ?? null;
  const [organizationLogoFailed, setOrganizationLogoFailed] = useState(false);

  useEffect(() => {
    setOrganizationLogoFailed(false);
  }, [organizationLogoUrl]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path === '/training') {
      return location.pathname.startsWith('/flows') || location.pathname === path || location.pathname.startsWith(`${path}/`);
    }
    if (path.startsWith('/mentorship')) {
      return location.pathname.startsWith('/mentorship');
    }
    return location.pathname === path;
  };
  const handleNavigate = useCallback((path: string) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  }, [navigate]);

  // Highlight a tapped tab immediately rather than after the next route renders.
  const [pendingTabPath, setPendingTabPath] = useState<string | null>(null);
  useEffect(() => { setPendingTabPath(null); }, [location.pathname]);
  const isTabActive = (path: string) => (pendingTabPath ? pendingTabPath === path : isActive(path));

  const handleLogoClick = () => {
    track('button_click', { action: 'header_logo_clicked_' });
    handleNavigate('/');
  };

  const handleSignOut = useCallback(() => {
    setIsMobileMenuOpen(false);
    logout();
  }, [logout]);

  return (
    <>
      <header
        className={cn('hidden w-full items-center justify-between lg:flex', isAuthenticated && 'signed-in-header')}
        data-testid="desktop-app-header"
      >
        <div className="app-header-brand-nav flex items-center gap-8">
          <button
            type="button"
            onClick={handleLogoClick}
            className="app-header-logo flex items-center focus-visible:outline-none"
            aria-label="Go to home"
          >
            {organizationLogoUrl && !organizationLogoFailed ? (
              <img
                src={organizationLogoUrl}
                alt={`${user?.organization?.name ?? 'Organization'} logo`}
                className="h-8 w-8 object-contain"
                onError={() => setOrganizationLogoFailed(true)}
              />
            ) : (
              <Icon name="logo" size={32} />
            )}
          </button>

          <nav className="app-header-navigation flex items-center gap-6">
            {navItems.map((item: NavItem) => (
              <NavigatorButton
                key={item.label}
                icon={item.icon}
                text={item.label}
                aria-current={isActive(item.path) ? 'page' : undefined}
                iconClassName={cn(
                  'text-foundation-base-darkbase-dark-300 [&_path]:stroke-current',
                  isActive(item.path) && 'text-foundation-orange-500'
                )}
                onClick={() => {
                  track('button_click', { action: `header_button_${item.label}` });
                  handleNavigate(item.path);
                }}
              />
            ))}
          </nav>
        </div>

        <div className="app-header-actions flex items-center gap-6">
          {user?.is_staff && (
            <NavigatorButton
              className="app-header-admin"
              icon="Shield"
              text="Admin"
              iconClassName="text-foundation-base-darkbase-dark-300"
              onClick={() => {
                track('button_click', { action: 'header_button_Admin' });
                handleNavigate('/admin');
              }}
            />
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="app-header-help group flex items-center gap-2 focus-visible:outline-none">
                <span className="whitespace-nowrap">Help</span>
                <ChevronDownIcon className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[13.75rem]">
              <DropdownMenuItem key="resources" onClick={() => handleNavigate('/help/resources')}>
                Emergency Resources
              </DropdownMenuItem>
              <DropdownMenuItem key="faq" onClick={() => handleNavigate('/help/faq')}>
                FAQs
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="app-header-profile group flex items-center gap-2 focus-visible:outline-none" aria-label="My profile">
                <Avatar className="app-header-avatar h-10 w-10">
                  {avatarUrl ? <AvatarImage src={avatarUrl} alt="Profile photo" /> : null}
                  <AvatarFallback
                    className={cn(
                      'font-semibold uppercase',
                      user?.profile_color ? undefined : 'bg-foundation-purplepurple-500 text-white'
                    )}
                    style={{ backgroundColor: avatarBackground, color: avatarTextColor }}
                  >
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <span className="whitespace-nowrap">My Profile</span>
                <ChevronDownIcon className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[13.75rem]">
              <DropdownMenuLabel className="text-xs uppercase text-muted-foreground">Signed in as</DropdownMenuLabel>
              <div className="px-3 py-1 text-sm font-medium">{user?.username || 'User'}</div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleNavigate('/profile')}>
                Profile
              </DropdownMenuItem>
              {canAccessOrganizationAdmin ? (
                <DropdownMenuItem onClick={() => handleNavigate('/organization/admin')}>
                  My Organization
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-destructive">
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <header
        className={cn('flex w-full items-center justify-between lg:hidden', isAuthenticated && 'signed-in-header')}
        data-testid="responsive-app-header"
      >
        <button
          type="button"
          onClick={handleLogoClick}
          className="app-header-logo flex items-center focus-visible:outline-none"
          aria-label="Go to home"
        >
          {organizationLogoUrl && !organizationLogoFailed ? (
            <img
              src={organizationLogoUrl}
              alt={`${user?.organization?.name ?? 'Organization'} logo`}
              className="h-8 w-8 object-contain"
              onError={() => setOrganizationLogoFailed(true)}
            />
          ) : (
            <Icon name="logo" size={30} />
          )}
        </button>

        <div className="flex items-center gap-2">
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                className="app-header-menu inline-flex h-10 w-10 items-center justify-center rounded-full border border-border/80 bg-white/80 text-foundation-base-darkbase-dark-500"
                aria-label="Open menu"
              >
                <MenuIcon className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent
              overlayClassName="bg-black/70 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
              className="w-[85vw] border-border bg-background p-0 text-foreground"
            >
              <div className="flex h-full flex-col">
                <div className="flex items-center justify-between border-b border-border px-4 py-4">
                  <div>
                    <SheetTitle className="text-sm font-semibold">Menu</SheetTitle>
                    <SheetDescription className="sr-only">
                      Navigate WiseFriend and manage your account.
                    </SheetDescription>
                  </div>
                  <SheetClose asChild>
                    <button
                      type="button"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground"
                      aria-label="Close menu"
                    >
                      <CloseIcon className="h-4 w-4" />
                    </button>
                  </SheetClose>
                </div>

                <div className="border-b border-border px-4 py-4">
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 text-left"
                    onClick={() => handleNavigate('/profile')}
                  >
                    <Avatar className="h-10 w-10">
                      {avatarUrl ? <AvatarImage src={avatarUrl} alt="Profile photo" /> : null}
                      <AvatarFallback
                        className={cn(
                          'font-semibold uppercase',
                          user?.profile_color ? undefined : 'bg-foundation-purplepurple-500 text-white'
                        )}
                        style={{ backgroundColor: avatarBackground, color: avatarTextColor }}
                      >
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-foreground">{user?.username || 'User'}</p>
                      <p className="text-xs text-muted-foreground">My Profile</p>
                    </div>
                  </button>
                  {canAccessOrganizationAdmin ? (
                    <button
                      type="button"
                      className="mt-3 w-full rounded-xl border border-border px-3 py-2 text-left text-sm font-medium text-foreground hover:bg-muted/50"
                      onClick={() => handleNavigate('/organization/admin')}
                    >
                      My Organization
                    </button>
                  ) : null}
                </div>

                <div className="flex-1 overflow-y-auto px-2 py-3">
                  {navItems.map((item: NavItem) => (
                    <button
                      key={item.label}
                      type="button"
                      className={cn(
                        'flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm transition-colors',
                        isActive(item.path)
                          ? 'bg-foundation-orange-50 text-foundation-orange-500'
                          : 'text-foreground hover:bg-muted/50'
                      )}
                      onClick={() => {
                        track('button_click', { action: `mobile_nav_${item.label}` });
                        handleNavigate(item.path);
                      }}
                    >
                      <Icon name={item.icon} size={18} />
                      <span>{item.label}</span>
                    </button>
                  ))}

                  <div className="my-2 border-t border-border" />

                  <button
                    type="button"
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm text-foreground hover:bg-muted/50"
                    onClick={() => handleNavigate('/help/resources')}
                  >
                    <Icon name="caution" size={18} />
                    <span>Emergency Resources</span>
                  </button>
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm text-foreground hover:bg-muted/50"
                    onClick={() => handleNavigate('/help/faq')}
                  >
                    <Icon name="help" size={18} />
                    <span>FAQs</span>
                  </button>
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm text-foreground hover:bg-muted/50"
                    onClick={() => handleNavigate('/contact')}
                  >
                    <Icon name="MessageCircle" size={18} />
                    <span>Contact us</span>
                  </button>

                  {user?.is_staff ? (
                    <>
                      <div className="my-2 border-t border-border" />
                      <button
                        type="button"
                        className={cn(
                          'flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm transition-colors',
                          location.pathname.startsWith('/admin')
                            ? 'bg-foundation-orange-50 text-foundation-orange-500'
                            : 'text-foreground hover:bg-muted/50',
                        )}
                        onClick={() => {
                          track('button_click', { action: 'mobile_nav_Admin' });
                          handleNavigate('/admin');
                        }}
                      >
                        <Icon name="Shield" size={18} />
                        <span>Admin</span>
                      </button>
                    </>
                  ) : null}
                </div>

                <div className="border-t border-border px-4 py-4">
                  <div className="flex flex-col gap-2">
                    <button
                      type="button"
                      className="w-full rounded-xl border border-border px-3 py-2 text-sm font-semibold text-destructive"
                      onClick={handleSignOut}
                    >
                      Sign out
                    </button>
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>
      {mobilePrimaryNav.length > 0 ? (
        <nav
          className={cn('mobile-tab-bar fixed inset-x-0 bottom-0 z-[60] border-t backdrop-blur-md lg:hidden', isAuthenticated && 'signed-in-header')}
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          aria-label="Primary mobile navigation"
          data-mobile-tab-bar
          data-testid="responsive-primary-navigation"
        >
          <div
            className="mx-auto grid max-w-screen-sm"
            style={{ gridTemplateColumns: `repeat(${mobilePrimaryNav.length}, minmax(0, 1fr))` }}
          >
            {mobilePrimaryNav.map((item: NavItem) => (
              <button
                key={`mobile-tab-${item.label}`}
                type="button"
                onClick={() => {
                  track('button_click', { action: `mobile_tab_${item.label}` });
                  nativeTapFeedback();
                  setPendingTabPath(item.path);
                  handleNavigate(item.path);
                }}
                className="mobile-tab flex min-w-0 flex-col items-center gap-1 px-1 pb-2 pt-2.5 text-[13px] leading-4"
                aria-current={isTabActive(item.path) ? 'page' : undefined}
              >
                <span className="mobile-tab-pill" aria-hidden="true">
                  <Icon name={item.icon} size={22} strokeWidth={1.75} />
                </span>
                <span className="truncate">{item.label}</span>
              </button>
            ))}
          </div>
        </nav>
      ) : null}
    </>
  );
};
